import React, { useState, useEffect, useCallback } from "react";
import {
  Alert,
  Box,
  Button,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { Backup, Schedule, Save } from "@mui/icons-material";
import { mapBackupStatus } from "../../utils/backupStatus";
import { SPACING, ICON_SIZES } from "../../theme/constants";
import { flexBetween } from "../../theme/responsive";
import { getBackupSchedule, updateBackupSchedule } from "../../services/Backup";

const DAYS_OF_WEEK = [
  { value: 0, label: "Sunday" },
  { value: 1, label: "Monday" },
  { value: 2, label: "Tuesday" },
  { value: 3, label: "Wednesday" },
  { value: 4, label: "Thursday" },
  { value: 5, label: "Friday" },
  { value: 6, label: "Saturday" },
];

/**
 * Site backups tab showing schedule config and all backups
 */
const SiteBackupsTab = ({
  siteId,
  backups,
  createLoading,
  onCreateBackup,
  onManageBackup,
}) => {
  const [schedule, setSchedule] = useState(null);
  const [scheduleLoading, setScheduleLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState(null);

  // Schedule form state
  const [enabled, setEnabled] = useState(false);
  const [frequency, setFrequency] = useState("daily");
  const [time, setTime] = useState("03:00");
  const [day, setDay] = useState(0);
  const [retentionCount, setRetentionCount] = useState(7);
  const [includeFiles, setIncludeFiles] = useState(true);
  const [includeDatabase, setIncludeDatabase] = useState(true);

  const loadSchedule = useCallback(async () => {
    if (!siteId) return;
    try {
      setScheduleLoading(true);
      const response = await getBackupSchedule(siteId);
      const data = response.data;
      setSchedule(data);
      setEnabled(data.enabled || false);
      setFrequency(data.frequency || "daily");
      setTime(data.time || "03:00");
      setDay(data.day ?? 0);
      setRetentionCount(data.retentionCount ?? 7);
      setIncludeFiles(data.includeFiles ?? true);
      setIncludeDatabase(data.includeDatabase ?? true);
    } catch {
      // Schedule not configured yet, use defaults
    } finally {
      setScheduleLoading(false);
    }
  }, [siteId]);

  useEffect(() => {
    loadSchedule();
  }, [loadSchedule]);

  const handleSaveSchedule = async () => {
    try {
      setSaving(true);
      setSaveMessage(null);
      const response = await updateBackupSchedule(siteId, {
        enabled,
        frequency,
        time,
        day: frequency === "daily" ? null : day,
        retentionCount,
        includeFiles,
        includeDatabase,
      });
      setSchedule(response.data);
      setSaveMessage({ type: "success", text: "Backup schedule saved" });
      setTimeout(() => setSaveMessage(null), 3000);
    } catch {
      setSaveMessage({ type: "error", text: "Failed to save schedule" });
    } finally {
      setSaving(false);
    }
  };

  // Format UTC time to show local equivalent
  const formatTimeInfo = (utcTime) => {
    if (!utcTime) return "";
    const [h, m] = utcTime.split(":").map(Number);
    const utcDate = new Date(Date.UTC(2024, 0, 1, h, m));
    const localTime = utcDate.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
    });
    return `${utcTime} UTC (${localTime} local)`;
  };

  return (
    <CardContent>
      {/* Schedule Configuration Section */}
      <Box sx={{ mb: SPACING.lg }}>
        <Box sx={{ ...flexBetween, mb: SPACING.md }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Schedule color="primary" />
            <Typography variant="h6" fontWeight={600}>
              Automatic Backups
            </Typography>
          </Box>
        </Box>

        {scheduleLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
            <CircularProgress size={24} />
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={enabled}
                  onChange={(e) => setEnabled(e.target.checked)}
                />
              }
              label="Enable automatic backup schedule"
            />

            {enabled && (
              <>
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  <FormControl size="small" sx={{ minWidth: 140 }}>
                    <InputLabel>Frequency</InputLabel>
                    <Select
                      value={frequency}
                      onChange={(e) => setFrequency(e.target.value)}
                      label="Frequency"
                    >
                      <MenuItem value="daily">Daily</MenuItem>
                      <MenuItem value="weekly">Weekly</MenuItem>
                      <MenuItem value="monthly">Monthly</MenuItem>
                    </Select>
                  </FormControl>

                  <TextField
                    size="small"
                    label="Time (UTC)"
                    type="time"
                    value={time}
                    onChange={(e) => setTime(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    sx={{ width: 140 }}
                  />

                  {frequency === "weekly" && (
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                      <InputLabel>Day of Week</InputLabel>
                      <Select
                        value={day}
                        onChange={(e) => setDay(e.target.value)}
                        label="Day of Week"
                      >
                        {DAYS_OF_WEEK.map((d) => (
                          <MenuItem key={d.value} value={d.value}>
                            {d.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}

                  {frequency === "monthly" && (
                    <TextField
                      size="small"
                      label="Day of Month"
                      type="number"
                      value={day}
                      onChange={(e) => setDay(parseInt(e.target.value) || 1)}
                      InputProps={{ inputProps: { min: 1, max: 28 } }}
                      sx={{ width: 140 }}
                    />
                  )}

                  <TextField
                    size="small"
                    label="Keep (count)"
                    type="number"
                    value={retentionCount}
                    onChange={(e) =>
                      setRetentionCount(parseInt(e.target.value) || 1)
                    }
                    InputProps={{ inputProps: { min: 1, max: 365 } }}
                    sx={{ width: 120 }}
                  />
                </Box>

                <Box sx={{ display: "flex", gap: 2 }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={includeFiles}
                        onChange={(e) => setIncludeFiles(e.target.checked)}
                        size="small"
                      />
                    }
                    label="Include files"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={includeDatabase}
                        onChange={(e) => setIncludeDatabase(e.target.checked)}
                        size="small"
                      />
                    }
                    label="Include database"
                  />
                </Box>

                {schedule?.nextBackupAt && (
                  <Typography variant="body2" color="text.secondary">
                    Next backup:{" "}
                    {new Date(schedule.nextBackupAt).toLocaleString()}
                    {" · "}
                    Time: {formatTimeInfo(time)}
                  </Typography>
                )}
                {schedule?.lastBackupAt && (
                  <Typography variant="body2" color="text.secondary">
                    Last backup:{" "}
                    {new Date(schedule.lastBackupAt).toLocaleString()}
                  </Typography>
                )}
              </>
            )}

            <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
              <Button
                variant="contained"
                size="small"
                startIcon={saving ? <CircularProgress size={16} /> : <Save />}
                onClick={handleSaveSchedule}
                disabled={saving}
              >
                Save Schedule
              </Button>
              {saveMessage && (
                <Alert severity={saveMessage.type} sx={{ py: 0, px: 1 }}>
                  {saveMessage.text}
                </Alert>
              )}
            </Box>
          </Box>
        )}
      </Box>

      <Divider sx={{ mb: SPACING.lg }} />

      {/* Backup List Section */}
      <Box
        sx={{
          ...flexBetween,
          mb: SPACING.md,
        }}
      >
        <Typography variant="h6" fontWeight={600}>
          Site Backups
        </Typography>
        <Button
          variant="contained"
          startIcon={<Backup />}
          onClick={onCreateBackup}
          disabled={createLoading}
        >
          {createLoading ? "Creating..." : "Create Backup"}
        </Button>
      </Box>

      {backups.length === 0 ? (
        <Box sx={{ textAlign: "center", py: SPACING.xl }}>
          <Backup
            sx={{
              fontSize: ICON_SIZES.xxl,
              color: "text.secondary",
              mb: SPACING.sm,
            }}
          />
          <Typography variant="h6" gutterBottom>
            No backups found
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ mb: SPACING.md }}
          >
            Create your first backup to protect your site data
          </Typography>
          <Button
            variant="contained"
            startIcon={<Backup />}
            onClick={onCreateBackup}
          >
            Create First Backup
          </Button>
        </Box>
      ) : (
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Backup Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Size</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Created</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {backups.map((backup) => (
                <TableRow key={backup.id} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {backup.backupName}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={backup.backupType}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">
                      {backup.sizeMB ? `${backup.sizeMB} MB` : "Calculating..."}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={mapBackupStatus(backup.status)}
                      size="small"
                      color={
                        mapBackupStatus(backup.status) === "completed"
                          ? "success"
                          : mapBackupStatus(backup.status) === "failed"
                            ? "error"
                            : mapBackupStatus(backup.status) === "in_progress"
                              ? "info"
                              : "default"
                      }
                      sx={{ textTransform: "capitalize" }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {new Date(backup.createdAt).toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => onManageBackup(backup)}
                      disabled={
                        mapBackupStatus(backup.status) === "in_progress"
                      }
                    >
                      Manage
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </CardContent>
  );
};

export default SiteBackupsTab;
